/**
 * Pagar a mensalidade (ou a mensalidade antecipada do avanco): pela Pagar.me (PIX ou cartao, baixa na hora) ou, se o
 * cliente escolher, pelo PIX direto da marca, mandando o comprovante na mesma tela (spec 047, 2.10; o envio e o da spec
 * 039). Um administrador confere o comprovante e da a baixa, ou recusa com um motivo que o cliente le e manda outro. No Perfil, "Meus comprovantes" guarda todos. Comprovante de
 * outro pagamento vai pelo suporte (Vitor, 30/09). Valor, chave, QR, prazo e estado sao do backend: a tela so
 * mostra e pergunta.
 */
import { Aviso, Botao, Cartao, CartaoDeComprovante, ComprovanteDaCobranca, ErroApi, EsqueletoDeCartao, formatarReais, hojeEmSaoPaulo, IconeEnviar, ListaDeComprovantes, mensagemDe, Paginacao, paginar, Pilha, PixDaMarca, type Comprovante, type DadosDoEnvio, type PixParaPagar } from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useAcao, useApi } from "../acoes";
import { marca } from "../config";
import { useSessao } from "../sessao";
import { usePagina } from "../pagina";

/** Os comprovantes da pessoa, do mais novo pro mais velho. `ligado`: so busca quando ha o que mostrar. */
export function useComprovantes(ligado = true) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["comprovantes"], queryFn: () => ler<{ itens: Comprovante[] }>("/v1/me/comprovantes"), enabled: ligado });
}

/** Se a area oferece o PIX direto: so com a chave cadastrada no painel (sem ela, so a Pagar.me). */
function usePixDisponivel() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["pix-disponivel"], queryFn: () => ler<{ disponivel: boolean }>("/v1/me/pix/disponivel"), staleTime: 5 * 60_000 });
}

/**
 * Os dois jeitos de pagar uma cobranca em aberto (a mensalidade, o pedido que espera pagamento), a escolha do cliente
 * (Vitor, 11/10): o "Pagar" vai pra Pagar.me (PIX ou cartao, a baixa e na hora); o "Pagar pelo PIX direto" abre o PIX
 * da marca ali mesmo, com o envio do comprovante, que um administrador confere em ate 1 dia. Com um comprovante em
 * analise, nao oferece pagar de novo e mostra a confirmacao com o prazo; recusado, mostra o motivo e deixa pagar e
 * mandar outro. O PIX aberto fica no endereco (`?pagar=<id>`): quem
 * sai pro app do banco e volta (ou o navegador recarrega a aba) encontra o PIX onde deixou.
 */
export function PagarCobranca({ tipo, id, valorCentavos, podePagar }: { tipo: Comprovante["alvo"]["tipo"]; id: string; valorCentavos: number | null; podePagar: boolean }) {
  const q = useComprovantes();
  const direto = usePixDisponivel().data?.disponivel === true;
  const { postar } = useApi();
  const pagamento = useAcao();
  const [indo, setIndo] = useState(false);
  const pagarNaPagarme = () =>
    pagamento.rodar(async () => {
      const r = await postar<{ link: string }>(`/v1/me/${tipo === "MENSALIDADE" ? "mensalidades" : "solicitacoes"}/${id}/pagar`);
      setIndo(true);
      window.location.assign(r.link);
    });
  const [busca, setBusca] = useSearchParams();
  const aberto = busca.get("pagar") === id;
  const [focarEnvio, setFocarEnvio] = useState(false);
  const [prazo, setPrazo] = useState<string>();
  const lugar = useRef<HTMLDivElement>(null);
  const ultimo = q.data?.itens.find((c) => c.alvo.tipo === tipo && c.alvo.id === id) ?? null;
  const emAnalise = ultimo?.estado === "EM_ANALISE";
  const mudar = (abrir: boolean) => {
    const p = new URLSearchParams(busca);
    if (abrir) p.set("pagar", id);
    else p.delete("pagar");
    setBusca(p, { replace: true });
  };
  // Voltou do banco com a aba recarregada: o PIX aberto aparece na tela (uma vez, quando a lista chega).
  const rolou = useRef(false);
  useEffect(() => {
    if (q.isLoading || rolou.current) return;
    rolou.current = true;
    if (aberto) lugar.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [q.isLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.isLoading) return null;
  const valor = valorCentavos != null ? formatarReais(valorCentavos) : "";
  const abrir = (envio: boolean) => { setFocarEnvio(envio); mudar(true); };
  const acoes = !emAnalise && podePagar && !aberto ? (
    <>
      <Botao carregando={pagamento.carregando || indo} onClick={() => void pagarNaPagarme()}>{`Pagar ${valor}`.trim()}</Botao>
      {direto && <Botao variante="secundario" onClick={() => abrir(false)}>Pagar pelo PIX direto</Botao>}
      {direto && ultimo?.estado === "RECUSADO" && <Botao variante="secundario" className="mtc-cobranca__enviar" icone={<IconeEnviar tamanho={18} />} onClick={() => abrir(true)}>Enviar outro comprovante</Botao>}
    </>
  ) : undefined;
  return (
    <div ref={lugar} className="mtc-pilha" style={{ gap: "var(--e-3)", scrollMarginTop: "calc(var(--e-8) * 2)" }}>
      <ComprovanteDaCobranca ultimo={ultimo} prazo={prazo} acoes={acoes} nota={direto ? "Pagar: PIX ou cartão, com confirmação na hora. PIX direto: você paga na chave e envia o comprovante; a liberação leva até 1 dia." : undefined} />
      {pagamento.erro && <Aviso tipo="erro">{pagamento.erro}</Aviso>}
      {!emAnalise && podePagar && aberto && direto && <PixAberto tipo={tipo} id={id} focarEnvio={focarEnvio} aoFechar={() => mudar(false)} aoEnviar={(p) => { setPrazo(p); mudar(false); }} />}
    </div>
  );
}

/** Tentativas do envio quando a rede cai ou o servidor esta subindo (502/503/504); erro que o backend explicou nao repete. */
const TENTATIVAS = 3;
const esperar = (ms: number) => new Promise((ok) => setTimeout(ok, ms));
const repetivel = (e: unknown) => !(e instanceof ErroApi) || e.status === 0 || e.status >= 502;

/**
 * O PIX desta cobranca (do backend) e o envio do comprovante. Enviou: fecha e a cobranca mostra "em analise" com o prazo.
 * O envio aguenta rede ruim (28/10, "trabalhe nos casos de envio pra que nao volte a dar erro"): sem resposta, olha
 * se o comprovante chegou mesmo assim (a resposta pode ter se perdido no caminho de volta) e tenta de novo; nao dando,
 * diz o que fazer, com o WhatsApp do suporte ao lado. Toda falha no aparelho vai pro log da API (`/falhas`), porque sem
 * isso ela so existe no celular da pessoa.
 */
function PixAberto({ tipo, id, focarEnvio, aoFechar, aoEnviar }: { tipo: Comprovante["alvo"]["tipo"]; id: string; focarEnvio: boolean; aoFechar: () => void; aoEnviar: (prazo: string) => void }) {
  const { ler, enviar, postar } = useApi();
  const qc = useQueryClient();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string>();
  const hoje = hojeEmSaoPaulo();
  const [v, setV] = useState<DadosDoEnvio>({ arquivo: null, dia: hoje, observacao: "" });
  const pix = useQuery({ queryKey: ["pix", tipo, id], queryFn: () => ler<PixParaPagar>(`/v1/me/pix?alvo_tipo=${tipo}&alvo_id=${encodeURIComponent(id)}`), retry: false, staleTime: 60_000 });
  const registrar = (falha: Record<string, unknown>) =>
    void postar("/v1/me/comprovantes/falhas", { ...falha, alvo_tipo: tipo, navegador: navigator.userAgent.slice(0, 300) }).catch(() => undefined);
  // Chegou mesmo sem a resposta: um comprovante desta cobranca em analise, enviado depois que o envio comecou.
  const chegou = async (desde: number) => {
    try {
      const r = await ler<{ itens: Comprovante[] }>("/v1/me/comprovantes");
      return r.itens.some((c) => c.alvo.tipo === tipo && c.alvo.id === id && c.estado === "EM_ANALISE" && Date.parse(c.enviado_em) >= desde - 60_000);
    } catch {
      return false;
    }
  };
  const mandar = async () => {
    const arquivo = v.arquivo!;
    const montar = () => {
      const form = new FormData();
      form.append("alvo_tipo", tipo);
      form.append("alvo_id", id);
      form.append("pago_no_dia", v.dia);
      if (v.observacao.trim()) form.append("observacao", v.observacao.trim());
      // O arquivo por ultimo: o backend le os campos antes dele.
      form.append("arquivo", arquivo);
      return form;
    };
    setEnviando(true);
    setErro(undefined);
    const desde = Date.now();
    let falha: unknown;
    try {
      for (let tentativa = 1; tentativa <= TENTATIVAS; tentativa++) {
        try {
          await enviar("/v1/me/comprovantes", montar());
          falha = undefined;
          break;
        } catch (e) {
          falha = e;
          // 409 depois de uma tentativa sem resposta: e o proprio comprovante, que chegou na tentativa anterior.
          const talvezChegou = repetivel(e) || (e instanceof ErroApi && e.status === 409 && tentativa > 1);
          if (talvezChegou && (await chegou(desde))) {
            falha = undefined;
            break;
          }
          if (!repetivel(e) || tentativa === TENTATIVAS) break;
          await esperar(1500 * tentativa);
        }
      }
      if (falha) {
        registrar({ etapa: "envio", motivo: falha instanceof ErroApi ? `HTTP ${falha.status} ${falha.message}`.slice(0, 200) : `${(falha as Error)?.name}: ${(falha as Error)?.message}`.slice(0, 200), tipo: arquivo.type || "(sem tipo)", tamanho: arquivo.size, codigo: falha instanceof ErroApi ? falha.codigo : undefined });
        setErro(repetivel(falha)
          ? "O comprovante não chegou até nós: a internet caiu no meio do envio. Confira a conexão e toque em Enviar de novo. Se continuar, fale com o suporte."
          : mensagemDe(falha, "Não deu certo agora. Tente de novo em instantes."));
        return;
      }
    } finally {
      setEnviando(false);
    }
    // Primeiro a lista: fechar antes dela chegar mostraria o "Pagar" de novo por um instante.
    await qc.invalidateQueries({ queryKey: ["comprovantes"] });
    aoEnviar(pix.data?.prazo ?? "");
    // O comprovante em analise muda a frase do bloqueio e o passo do plano: o resto busca de novo.
    void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "comprovantes" });
  };
  const w = marca().whatsapp;
  const suporte = w ? `${w}${w.includes("?") ? "&" : "?"}text=${encodeURIComponent("Olá! Quero pagar pela área, mas o PIX não abriu.")}` : null;
  return (
    <PixDaMarca
      pix={pix.data}
      carregando={pix.isLoading}
      erro={pix.isError ? mensagemDe(pix.error, "O PIX não abriu agora. Tente de novo em instantes.") : undefined}
      suporte={suporte}
      focarEnvio={focarEnvio}
      aoFechar={aoFechar}
      envio={{ valor: v, aoMudar: (nv) => { setV(nv); setErro(undefined); }, hoje, enviando, erro, aoEnviar: () => void mandar(), aoFalhar: (f) => registrar(f) }}
    />
  );
}

/** Link do WhatsApp da marca com a mensagem pronta pra comprovante de outro pagamento (o mesmo numero do suporte, no menu da conta). */
function whatsappDeOutroPagamento(nome: string | undefined): string | null {
  const w = marca().whatsapp;
  if (!w) return null;
  const texto = `Olá! Sou ${nome ? nome.split(" ")[0] : "cliente"} e quero enviar o comprovante de um pagamento que não é mensalidade.`;
  return `${w}${w.includes("?") ? "&" : "?"}text=${encodeURIComponent(texto)}`;
}

/** Perfil > Meus comprovantes: tudo o que a pessoa mandou, o estado, o motivo quando recusado e o arquivo pra baixar. */
export function MeusComprovantes() {
  const pagComp = usePagina("pagina_comprovantes");
  const q = useComprovantes();
  const { arquivo, estado } = useSessao();
  const [baixando, setBaixando] = useState<string>();
  const [erro, setErro] = useState<string>();
  const baixar = async (c: Comprovante) => {
    setErro(undefined);
    setBaixando(c.id);
    try {
      const blob = await arquivo(`/v1/me/comprovantes/${c.id}/arquivo`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = c.arquivo.nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setErro(mensagemDe(e, "Não consegui baixar agora. Tente de novo em instantes."));
    } finally {
      setBaixando(undefined);
    }
  };
  const zap = whatsappDeOutroPagamento(estado.status === "logado" ? estado.usuario.nome : undefined);
  const itens = q.data?.itens ?? [];
  return (
    <div id="comprovantes">
      <Cartao>
        <Pilha espaco={4}>
          <div style={{ display: "grid", gap: 2 }}>
            <h2 className="mtc-rotulo" style={{ margin: 0 }}>Meus comprovantes</h2>
            <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Os que você enviou ao pagar a mensalidade ou o pedido de avanço pelo PIX direto, e o que o administrador decidiu.</span>
          </div>
          {erro && <Aviso tipo="erro">{erro}</Aviso>}
          {q.isLoading ? (
            <EsqueletoDeCartao linhas={2} rotulo="Buscando seus comprovantes" />
          ) : q.isError ? (
            <Aviso tipo="erro">{mensagemDe(q.error, "Não consegui buscar seus comprovantes agora. Recarregue a página.")}</Aviso>
          ) : !itens.length ? (
            <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Você ainda não enviou comprovante. Ele é pra quem paga pelo PIX direto: em Mensalidades, no botão Pagar pelo PIX direto, você paga e envia o comprovante ali mesmo.</p>
          ) : (
            <ListaDeComprovantes rotulo="Meus comprovantes">
              {paginar(itens, pagComp.pagina).map((c) => (
                <div role="listitem" key={c.id}>
                  <CartaoDeComprovante c={c} rotuloDaObservacao="Sua observação" acoes={<Botao variante="secundario" tamanho="pequeno" carregando={baixando === c.id} onClick={() => void baixar(c)}>{c.arquivo.tipo.startsWith("image/") ? "Baixar a imagem" : "Baixar o PDF"}</Botao>} />
                </div>
              ))}
            </ListaDeComprovantes>
          )}
          {itens.length > 0 && <Paginacao pagina={pagComp.pagina} total={itens.length} aoMudar={pagComp.aoMudar} rotulo="Páginas dos comprovantes" />}
          <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>
            Comprovante de outro pagamento? {zap ? <a className="mtc-link" href={zap} target="_blank" rel="noreferrer">Fale com o suporte</a> : "Fale com o suporte"} e diga do que ele é.
          </p>
        </Pilha>
      </Cartao>
    </div>
  );
}
